/**
 * Lightweight typed i18n runtime (I18N-001/002/009/010, FR-CUS-143).
 *
 * - `t(key, params?)` — non-hook lookup with `{placeholder}` interpolation,
 *   usable anywhere (domain modules, callbacks, toasts).
 * - `useT()` — hook variant that also subscribes the component to locale
 *   changes so translated text re-renders on switch (no full reload,
 *   I18N-009).
 * - Locale defaults to `th` (Thai-first product), persists per device in
 *   localStorage, and mirrors onto `document.documentElement.lang`
 *   (I18N-010 / A11Y-025).
 * - Missing-key fallback chain: active locale → `th` → the key name itself —
 *   never a blank (I18N-002). Both catalogs are compile-time-checked to have
 *   identical keys, and `i18n.test.ts` asserts parity at runtime too.
 */
import { useSyncExternalStore } from "react";
import { en } from "./catalog.en.ts";
import { type MessageKey, th } from "./catalog.th.ts";

export type Locale = "th" | "en";
export type { MessageKey };

export const LOCALE_STORAGE_KEY = "jod.customer.locale";
export const SUPPORTED_LOCALES: readonly Locale[] = ["th", "en"];

const catalogs: Record<Locale, Record<MessageKey, string>> = { th, en };

function isLocale(value: unknown): value is Locale {
  return value === "th" || value === "en";
}

function readStoredLocale(): Locale {
  if (typeof window === "undefined") return "th";
  try {
    const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    return isLocale(stored) ? stored : "th";
  } catch {
    // localStorage is best-effort in LIFF webviews and test sandboxes.
    return "th";
  }
}

function applyDocumentLang(locale: Locale): void {
  if (typeof document !== "undefined") {
    document.documentElement.lang = locale;
  }
}

let currentLocale: Locale = readStoredLocale();
// Reflect a persisted non-default choice on boot (index.html ships lang="th").
applyDocumentLang(currentLocale);

const listeners = new Set<() => void>();

export function getLocale(): Locale {
  return currentLocale;
}

export function setLocale(next: Locale): void {
  if (next === currentLocale) return;
  currentLocale = next;
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, next);
  } catch {
    // Persisting the choice is best-effort; the in-memory locale still applies.
  }
  applyDocumentLang(next);
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export type TranslateParams = Record<string, string | number>;

/**
 * Non-hook translate. Safe outside React (session guards, domain display
 * helpers, toast copy) — reads the module-level current locale at call time.
 */
export function t(key: MessageKey, params?: TranslateParams): string {
  const message = catalogs[currentLocale][key] ?? th[key] ?? key;
  if (!params) return message;
  return message.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in params ? String(params[name]) : match,
  );
}

/** Current locale as reactive React state. */
export function useLocale(): Locale {
  return useSyncExternalStore(subscribe, getLocale, () => "th");
}

/**
 * Hook translate: identical signature to `t`, but subscribes the component
 * to locale changes so strings update in place when the guest switches.
 */
export function useT(): typeof t {
  useLocale();
  return t;
}
